/**
 * The map. One screen, composed from the platform's published facts.
 *
 * Everything here reads. The only writes in this file are to the DOM.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { buildMap, routePath, WORLD_WIDTH, type Station } from "./layout";
import { StationNode } from "./Station";
import { StationCard } from "./StationCard";
import { Receipt, type ReceiptContent } from "./Receipt";
import { packAsset } from "./assets";
import { TickIcon } from "./icons";
import { notifyShell } from "../data/session";
import type { MeProgress, WidgetConfig } from "../data/contract";

interface Props {
  config: WidgetConfig;
  progress: MeProgress;
  /** Player facing words per campaign id, from the vocabulary pack. */
  copy: Record<string, { title: string; desc: string }>;
  receipt: ReceiptContent | null;
}

const MOTE_COUNT = 16;

export function MapScreen({ config, progress, copy, receipt: initialReceipt }: Props) {
  const scroller = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState<{ station: Station; closing: boolean } | null>(null);
  const [receipt, setReceipt] = useState<ReceiptContent | null>(initialReceipt);
  const [entered, setEntered] = useState(false);

  // Coins follow the store. Off, and no coin surface is drawn anywhere: no
  // chip, no balance, no coin amount on a card or a receipt. Owner decision 9.
  const showCoins = config.surfaces.store;

  const model = useMemo(
    () => buildMap({ campaigns: progress.campaigns, season: progress.season, showCoins }),
    [progress.campaigns, progress.season, showCoins],
  );

  const balance = progress.balances.find((b) => b.currency === "coins")?.amount ?? 0;

  // The rail shows position in the whole season rather than in this viewport.
  const railPct = useMemo(() => {
    const summitY = model.summit?.y ?? 0;
    const span = model.worldHeight - summitY;
    const hot = model.stations[model.hotIndex];
    const refY = hot ? hot.y : summitY;
    return Math.max(0, Math.min(1, (model.worldHeight - refY) / span)) * 100;
  }, [model]);

  const titleFor = useCallback(
    (station: Station): string => {
      if (station.kind === "reward") return station.marker?.label ?? "Reward";
      if (station.kind === "gate") return station.gate?.label ?? "Gate";
      return copy[station.id]?.title ?? "Quest";
    },
    [copy],
  );

  // Entrance choreography, once, after the first paint.
  useEffect(() => {
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, []);

  // Open on the hot station, so a returning player lands on what is next
  // rather than at an arbitrary end of a route whose length they did not pick.
  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const hot = model.stations[model.hotIndex];
    el.scrollTop = hot ? Math.max(0, hot.y - 340) : 0;
  }, [model]);

  // Parallax: the world drifts slower than the route above it.
  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const bg = el.querySelector<HTMLElement>(".world-bg");
    if (!bg) return;
    let frame = 0;
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        bg.style.transform = `translateY(${el.scrollTop * 0.12}px)`;
      });
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      el.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(frame);
    };
  }, []);

  const openStation = useCallback(
    (station: Station) => {
      setOpen({ station, closing: false });
      // The host page may consume this; ActiveKit collects nothing.
      notifyShell("station.opened", { kind: station.kind });
    },
    [],
  );

  const closeStation = useCallback(() => {
    setOpen((current) => (current ? { ...current, closing: true } : null));
    window.setTimeout(() => setOpen(null), 160);
  }, []);

  const points = useMemo<[number, number][]>(() => {
    const all = model.stations.map((s) => [s.x, s.y] as [number, number]);
    return model.summit ? [...all, [model.summit.x, model.summit.y + 66]] : all;
  }, [model]);

  // The route splits at the hot station: solid behind, dotted ahead.
  const { donePath, aheadPath } = useMemo(() => {
    const cut = model.hotIndex >= 0 ? model.hotIndex : model.stations.length;
    const done = points.slice(0, Math.max(2, cut + 1));
    const ahead = points.slice(Math.max(0, cut));
    return {
      donePath: routePath(done),
      aheadPath: model.hotIndex >= 0 ? routePath(ahead) : "",
    };
  }, [points, model]);

  const motes = useMemo(
    () =>
      Array.from({ length: MOTE_COUNT }, (_, i) => ({
        left: `${8 + ((i * 37) % 84)}%`,
        top: `${6 + ((i * 53) % 88)}%`,
        size: `${3 + (i % 3)}px`,
        duration: `${7 + (i % 5) * 1.6}s`,
        delay: `${-(i * 1.37) % 9}s`,
      })),
    [],
  );

  /**
   * The card is a child of the frame while a station's coordinates are in the
   * world, so the anchor converts between them: subtract the scroll, add where
   * the scroller itself sits under the chrome, then keep the card inside the
   * frame rather than letting a station near an edge push it out of view.
   */
  const anchorFor = (station: Station) => {
    const el = scroller.current;
    if (!el) return { left: station.x, top: station.y };
    const box = el.getBoundingClientRect();
    const frame = el.closest(".frame")?.getBoundingClientRect();
    const offsetX = frame ? box.left - frame.left : 0;
    const offsetY = frame ? box.top - frame.top : 0;
    const CARD_W = 246;
    const CARD_H = 190;
    const scale = box.width / WORLD_WIDTH;

    const left = Math.min(
      Math.max(offsetX + 12, offsetX + station.x * scale - CARD_W / 2),
      offsetX + box.width - CARD_W - 12,
    );
    // Above the station where there is room, below it otherwise.
    const above = station.y - el.scrollTop + offsetY - CARD_H - 46;
    const below = station.y - el.scrollTop + offsetY + 52;
    const top = above > offsetY + 8 ? above : below;
    return { left, top };
  };

  const seasonName = progress.season?.name ?? "Season";
  const summitEarned = model.summit?.earned ?? false;

  return (
    <div className="frame" data-template={config.template} data-standalone={window.parent === window}>
      <header className="chrome">
        <div>
          <div className="season-eyebrow">{seasonName}</div>
          <div className="season-name">Signal route</div>
        </div>
        <div className="chips">
          {showCoins && (
            <div className="chip">
              <img src={packAsset(config.template, "coin")} alt="" />
              <span className="tnum">{balance.toLocaleString("en-US")}</span>
            </div>
          )}
          {config.surfaces.streak && progress.streak && (
            <div className="chip">
              <svg className="flame" viewBox="0 0 17 20" fill="none" aria-hidden="true">
                <path d="M8.5 1C9.5 4 13.5 6.2 13.5 11a5 5 0 0 1-10 0C3.5 8 5 6.8 5.6 5.2 6.6 6.4 7 7.4 7 8.6 8.3 7 8.9 4.2 8.5 1z" fill="#f59e0b" />
                <path d="M8.5 12.2c.9 1 1.7 1.7 1.7 3.1a1.9 1.9 0 1 1-3.8 0c0-1.1.8-1.8 1.3-2.4.3.4.5.8.8 1.3z" fill="#ffd98a" />
              </svg>
              <span className="tnum">Day {progress.streak.current}</span>
            </div>
          )}
        </div>
      </header>

      <div className="body">
        <div className="viewport">
          <div className="goalbar">
            <img src={packAsset(config.template, "kit-chip")} alt="" />
            <div>
              <div className="g-name">{model.summit?.label ?? "Season reward"}</div>
              <div className="g-sub">
                {summitEarned ? `${seasonName} complete` : "Season reward, final tier"}
              </div>
            </div>
            <div className="g-far">
              <b className="tnum">{summitEarned ? "\u2713" : model.remaining}</b>
              <span>{summitEarned ? "earned" : model.remaining === 1 ? "quest away" : "quests away"}</span>
            </div>
          </div>

          <div className="rail">
            <i style={{ height: `${railPct}%` }} />
            <b style={{ bottom: `${railPct}%` }} />
          </div>

          <div className={`scroller${entered ? " enter" : ""}`} ref={scroller}>
          <div className="world" style={{ height: `${model.worldHeight}px` }}>
            <img className="world-bg" src={packAsset(config.template, "bg")} alt="" />

            {config.template !== "minimal" &&
              motes.map((m, i) => (
                <span
                  key={i}
                  className="mote"
                  style={{
                    left: m.left, top: m.top, width: m.size, height: m.size,
                    animationDuration: m.duration, animationDelay: m.delay,
                  }}
                />
              ))}

            <div className="zones">
              {model.zones.map((zone) => (
                <div className="zone" key={zone.name} style={{ top: `${zone.top}px`, height: `${zone.height}px` }}>
                  <i />
                  <div className="z-rule" />
                  <div className="z-name">{zone.name}</div>
                </div>
              ))}
            </div>

            <svg className="route" viewBox={`0 0 ${WORLD_WIDTH} ${model.worldHeight}`}
              preserveAspectRatio="none" aria-hidden="true">
              {aheadPath && (
                <path d={aheadPath} fill="none" stroke="var(--route-ahead)" strokeWidth="5"
                  strokeLinecap="round" strokeDasharray="1 14" />
              )}
              <path className="done-path" d={donePath} fill="none" stroke="var(--accent)"
                strokeWidth="5" strokeLinecap="round" opacity=".8" />
            </svg>

            {model.summit && (
              <div className={`summit${summitEarned ? " earned" : ""}`}
                style={{ left: `${model.summit.x}px`, top: `${model.summit.y}px` }}>
                <div className="summit-halo" />
                <img src={packAsset(config.template, "kit")} alt="" />
                <div className="summit-label">{model.summit.label}</div>
                <div className="summit-sub">
                  {summitEarned ? `${seasonName} complete` : "Finish the season"}
                </div>
              </div>
            )}

            <div className="nodes">
              {model.stations.map((station, i) => (
                <StationNode
                  key={station.id}
                  station={station}
                  template={config.template}
                  title={titleFor(station)}
                  pop={`${(0.25 + (model.stations.length - i) * 0.045).toFixed(3)}s`}
                  bob={{
                    animationDuration: `${(2.4 + ((i * 7) % 16) / 10).toFixed(2)}s`,
                    animationDelay: `${(-((i * 13) % 30) / 10).toFixed(2)}s`,
                  }}
                  showCoins={showCoins}
                  onOpen={openStation}
                />
              ))}
            </div>
            </div>
          </div>
        </div>

        {config.surfaces.checklist && (
          <aside className="panel">
            <h3>Today</h3>
            <div className="today">
              {progress.campaigns
                .filter((c) => c.enrollment !== "not_enrolled")
                .slice(0, 3)
                .map((c) => {
                  const done = c.enrollment === "completed" || c.completedAt !== null;
                  const pay =
                    c.reward?.reward.kind === "credits" ? Number(c.reward.reward.value ?? 0) : 0;
                  return (
                    <div className={`q${done ? " done" : ""}`} key={c.id}>
                      <div className="q-tick">{done ? <TickIcon /> : null}</div>
                      <div className="q-body">
                        <div className="q-name">{copy[c.id]?.title ?? "Quest"}</div>
                        <div className="q-meta">
                          {done
                            ? "Done"
                            : c.goal.target > 1
                              ? `${c.goal.current} of ${c.goal.target} done`
                              : "Not started"}
                        </div>
                      </div>
                      {showCoins && pay > 0 && (
                        <div className="q-pay tnum">
                          +{pay} <img src={packAsset(config.template, "coin")} alt="" />
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>

            <h3>Next on the route</h3>
            {(() => {
              const next = model.stations.find((s) => s.quest?.state === "hot") ?? null;
              if (!next?.quest) return <p className="note">Every station on the route is lit.</p>;
              const goal = next.quest.campaign.goal;
              return (
                <div className="nextup">
                  <div className="n-row">
                    <div>
                      <b>{titleFor(next)}</b>
                      <span className="tnum">{goal.current} of {goal.target}</span>
                    </div>
                  </div>
                  <div className="bar"><i style={{ width: `${Math.round(next.quest.fraction * 100)}%` }} /></div>
                </div>
              );
            })()}

            <p className="note">
              Everything on this screen reflects what you did in your own work. Nothing here is
              claimed or spent. Rewards arrive on their own.
            </p>
          </aside>
        )}
      </div>

      {open && (
        <StationCard
          station={open.station}
          title={titleFor(open.station)}
          description={
            open.station.kind === "quest"
              ? (copy[open.station.id]?.desc ?? "")
              : open.station.kind === "reward"
                ? "A reward on the season track. It lights up when it is yours."
                : "Everything past this point counts toward the season reward."
          }
          anchor={anchorFor(open.station)}
          showCoins={showCoins}
          closing={open.closing}
          onClose={closeStation}
        />
      )}

      {receipt && (
        <Receipt content={receipt} template={config.template} onDismiss={() => setReceipt(null)} />
      )}
    </div>
  );
}
